import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Autocomplete, Box, Button, TextField } from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import SolarPowerRoundedIcon from '@mui/icons-material/SolarPowerRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';

import circularProgress from '../../components/circularProgress';
import MonthRangePicker from '../../components/monthRangePicker';
import PageHeader from '../../components/pageHeader';
import { PageIntro } from '../../components/ui';
import {
    formatMonthParam,
    getCurrentMonthStart,
    getCurrentYearStart,
    getPreviousDayMonthStart
} from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatDecimal, formatPercentRatio } from '../../utils/numberUtils';
import '../css/realEstimatedBI.css';

const allOption = 'all';
const minMonth = new Date(2025, 0, 1);

const chartMetrics = [
    { key: 'geracao', label: 'Real', className: 'is-real' },
    { key: 'estimado', label: 'Estimado', className: 'is-estimated' },
    { key: 'perdas', label: 'Perdas', className: 'is-loss' }
];

const getOptionLabel = (option, allLabel, labelField = 'label') => {
    if (option === allOption) {
        return allLabel;
    }

    return option[labelField];
};

const getUniqueOptions = (rows, idField, labelField) => {
    const optionsById = new Map();

    rows.forEach((row) => {
        optionsById.set(row[idField], {
            id: row[idField],
            label: row[labelField]
        });
    });

    return [...optionsById.values()];
};

const ratio = (numerator, denominator) => (denominator > 0 ? numerator / denominator : null);

const formatRatio = (value) => (value === null ? '—' : formatPercentRatio(value));

const buildIndicators = (row) => ({
    aderenciaGarantida: ratio(row.geracao, row.contrato),
    aderenciaP50: ratio(row.geracao, row.estimado),
    disponibilidadeTecnica: ratio(row.geracao, row.geracao + row.perdas),
    disponibilidadeOM: ratio(row.geracao, row.geracao + row.perdas - row.perdas_distribuidora),
    fatorCapacidade: ratio(row.geracao, row.capacidade)
});

const buildSummary = (rows) => {
    const totals = rows.reduce((acc, row) => ({
        geracao: acc.geracao + row.geracao,
        estimado: acc.estimado + row.estimado,
        contrato: acc.contrato + row.contrato,
        perdas: acc.perdas + row.perdas,
        perdas_distribuidora: acc.perdas_distribuidora + row.perdas_distribuidora,
        capacidade: acc.capacidade + row.capacidade,
        inversor_dias_sem_falha: acc.inversor_dias_sem_falha + row.inversor_dias_sem_falha
    }), {
        geracao: 0,
        estimado: 0,
        contrato: 0,
        perdas: 0,
        perdas_distribuidora: 0,
        capacidade: 0,
        inversor_dias_sem_falha: 0
    });

    return {
        ...totals,
        ...buildIndicators(totals)
    };
};

const buildChartRows = (rows) => {
    const rowsByPeriod = new Map();

    rows.forEach((row) => {
        const periodRow = rowsByPeriod.get(row.periodo) || {
            periodo: row.periodo,
            geracao: 0,
            estimado: 0,
            perdas: 0
        };

        rowsByPeriod.set(row.periodo, {
            periodo: row.periodo,
            geracao: periodRow.geracao + row.geracao,
            estimado: periodRow.estimado + row.estimado,
            perdas: periodRow.perdas + row.perdas
        });
    });

    return [...rowsByPeriod.values()].sort((left, right) => left.periodo.localeCompare(right.periodo));
};

const RelatorioSpeChart = ({ rows }) => {
    const [activeTooltip, setActiveTooltip] = useState(null);
    const maxValue = Math.max(...rows.flatMap((row) => chartMetrics.map((metric) => row[metric.key])));
    const chartHeight = 360;
    const yTicks = [1, 0.8, 0.6, 0.4, 0.2, 0].map((ratio) => ({
        ratio,
        value: maxValue * ratio
    }));

    return (
        <Box className='real-estimated-bi-chart-panel' onMouseLeave={() => setActiveTooltip(null)}>
            <Box className='real-estimated-bi-chart-heading'>
                <span><InsightsRoundedIcon /> Real, estimado e perdas</span>
                <Box className='real-estimated-bi-chart-legend'>
                    {chartMetrics.map((metric) => (
                        <span key={metric.key} className={metric.className}>{metric.label}</span>
                    ))}
                </Box>
            </Box>

            {activeTooltip && (
                <Box className='real-estimated-bi-chart-tooltip'>
                    <span>{activeTooltip.periodo}</span>
                    <strong>{activeTooltip.label}: {formatDecimal(activeTooltip.value)} kWh</strong>
                </Box>
            )}

            <Box className='real-estimated-bi-column-chart' role='img' aria-label='Comparativo de geração real, estimada e perdas por período'>
                <Box className='real-estimated-bi-y-axis-title'>kWh</Box>
                <Box className='real-estimated-bi-y-axis'>
                    {yTicks.map((tick) => (
                        <span key={tick.ratio}>{formatDecimal(tick.value)}</span>
                    ))}
                </Box>
                <Box className='real-estimated-bi-plot' style={{ height: chartHeight }}>
                    {yTicks.map((tick) => (
                        <span
                            className='real-estimated-bi-grid-line'
                            key={tick.ratio}
                            style={{ bottom: `${tick.ratio * 100}%` }}
                        />
                    ))}

                    <Box className='real-estimated-bi-periods'>
                        {rows.map((row) => (
                            <Box className='real-estimated-bi-period-group' key={row.periodo}>
                                <Box className='real-estimated-bi-column-group'>
                                    {chartMetrics.map((metric) => (
                                        <Box className='real-estimated-bi-column-slot' key={metric.key}>
                                            <span
                                                aria-label={`${row.periodo} ${metric.label}: ${formatDecimal(row[metric.key])} kWh`}
                                                className={`real-estimated-bi-column ${metric.className}`}
                                                role='button'
                                                style={{ height: `${(row[metric.key] / maxValue) * 100}%` }}
                                                tabIndex={0}
                                                title={`${row.periodo} ${metric.label}: ${formatDecimal(row[metric.key])} kWh`}
                                                onBlur={() => setActiveTooltip(null)}
                                                onFocus={() => setActiveTooltip({
                                                    periodo: row.periodo,
                                                    label: metric.label,
                                                    value: row[metric.key]
                                                })}
                                                onMouseEnter={() => setActiveTooltip({
                                                    periodo: row.periodo,
                                                    label: metric.label,
                                                    value: row[metric.key]
                                                })}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                                <span className='real-estimated-bi-period-label'>{row.periodo}</span>
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box className='real-estimated-bi-x-axis-title'>Período</Box>
            </Box>
        </Box>
    );
};

const renderSiteCell = (params) => (
    <span className='real-estimated-bi-plant'>
        <SolarPowerRoundedIcon />
        {params.value}
    </span>
);

const getColumns = () => [
    {
        field: 'periodo',
        headerName: 'Período',
        width: 130,
        align: 'center',
        headerAlign: 'center'
    },
    {
        field: 'empresa',
        headerName: 'SPE',
        flex: 1,
        minWidth: 230
    },
    {
        field: 'site',
        headerName: 'Site',
        flex: 1,
        minWidth: 220,
        renderCell: renderSiteCell
    },
    {
        field: 'geracao',
        headerName: 'Real (kWh)',
        type: 'number',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => formatDecimal(params.value)
    },
    {
        field: 'estimado',
        headerName: 'Estimado (kWh)',
        type: 'number',
        width: 170,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => formatDecimal(params.value)
    },
    {
        field: 'perdas',
        headerName: 'Perdas (kWh)',
        type: 'number',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => formatDecimal(params.value)
    },
    {
        field: 'contrato',
        headerName: 'Contrato (kWh)',
        type: 'number',
        width: 170,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => formatDecimal(params.value)
    },
    {
        field: 'aderenciaGarantida',
        headerName: 'Aderência à energia garantida',
        type: 'number',
        width: 220,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => buildIndicators(row).aderenciaGarantida,
        renderCell: (params) => formatRatio(params.value)
    },
    {
        field: 'aderenciaP50',
        headerName: 'Aderência ao P50',
        type: 'number',
        width: 160,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => buildIndicators(row).aderenciaP50,
        renderCell: (params) => formatRatio(params.value)
    },
    {
        field: 'disponibilidadeTecnica',
        headerName: 'Disponibilidade energética técnica',
        type: 'number',
        width: 250,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => buildIndicators(row).disponibilidadeTecnica,
        renderCell: (params) => formatRatio(params.value)
    },
    {
        field: 'disponibilidadeOM',
        headerName: 'Disponibilidade energética O&M',
        type: 'number',
        width: 240,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => buildIndicators(row).disponibilidadeOM,
        renderCell: (params) => formatRatio(params.value)
    },
    {
        field: 'fatorCapacidade',
        headerName: 'Fator de capacidade',
        type: 'number',
        width: 170,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => buildIndicators(row).fatorCapacidade,
        renderCell: (params) => formatRatio(params.value)
    },
    {
        field: 'inversor_dias_sem_falha',
        headerName: 'Inversor-dias parados sem falha',
        type: 'number',
        width: 230,
        align: 'center',
        headerAlign: 'center'
    }
];

const RelatorioSpeBI = () => {
    const navigate = useNavigate();
    const [selectedEmpresa, setSelectedEmpresa] = useState(allOption);
    const [selectedSite, setSelectedSite] = useState(allOption);
    const [dataInicio, setDataInicio] = useState(getCurrentYearStart);
    const [dataFim, setDataFim] = useState(getCurrentMonthStart);
    const [allRows, setAllRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const initialFetchStarted = useRef(false);

    const rowsBySelectedSite = useMemo(() => {
        if (selectedSite === allOption) {
            return allRows;
        }

        return allRows.filter((row) => row.site_id === selectedSite.id);
    }, [allRows, selectedSite]);
    const rowsBySelectedEmpresa = useMemo(() => {
        if (selectedEmpresa === allOption) {
            return allRows;
        }

        return allRows.filter((row) => row.empresa_id === selectedEmpresa.id);
    }, [allRows, selectedEmpresa]);
    const empresaOptions = useMemo(() => [allOption, ...getUniqueOptions(rowsBySelectedSite, 'empresa_id', 'empresa')], [rowsBySelectedSite]);
    const siteOptions = useMemo(() => [allOption, ...getUniqueOptions(rowsBySelectedEmpresa, 'site_id', 'site')], [rowsBySelectedEmpresa]);
    const rows = useMemo(() => {
        return allRows.filter((row) => (
            (selectedEmpresa === allOption || row.empresa_id === selectedEmpresa.id) &&
            (selectedSite === allOption || row.site_id === selectedSite.id)
        ));
    }, [allRows, selectedEmpresa, selectedSite]);
    const summary = useMemo(() => buildSummary(rows), [rows]);
    const chartRows = useMemo(() => buildChartRows(rows), [rows]);
    const canDrillThrough = selectedEmpresa !== allOption && selectedSite !== allOption;

    const buscarRelatorioSpe = useCallback(async ({
        dataInicioValue,
        dataFimValue
    }) => {
        setIsLoading(true);

        const body = JSON.stringify({
            data_inicio: formatMonthParam(dataInicioValue),
            data_fim: formatMonthParam(dataFimValue)
        });

        const response = await fetchFlask('buscarRelatorioSpeBiss', body);
        setAllRows(response.usinas);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        async function fetchInitialData () {
            if (initialFetchStarted.current) {
                return;
            }

            initialFetchStarted.current = true;

            await buscarRelatorioSpe({
                dataInicioValue: dataInicio,
                dataFimValue: dataFim
            });

            setInitialDataLoaded(true);
        }

        fetchInitialData();
    }, []);

    useEffect(() => {
        if (!initialDataLoaded) return;

        buscarRelatorioSpe({
            dataInicioValue: dataInicio,
            dataFimValue: dataFim
        });
    }, [dataFim, dataInicio]);

    const handleDateRangeApply = (startDate, endDate) => {
        setDataInicio(startDate);
        setDataFim(endDate);
    };

    const handleEmpresaChange = (value) => {
        setSelectedEmpresa(value);

        if (
            value !== allOption &&
            selectedSite !== allOption &&
            !allRows.some((row) => row.empresa_id === value.id && row.site_id === selectedSite.id)
        ) {
            setSelectedSite(allOption);
        }
    };

    const handleSiteChange = (value) => {
        setSelectedSite(value);

        if (
            value !== allOption &&
            selectedEmpresa !== allOption &&
            !allRows.some((row) => row.site_id === value.id && row.empresa_id === selectedEmpresa.id)
        ) {
            setSelectedEmpresa(allOption);
        }
    };

    const handleDrillThrough = () => {
        const searchParams = new URLSearchParams({
            empresa_id: String(selectedEmpresa.id),
            site_id: String(selectedSite.id),
            data_inicio: formatMonthParam(dataInicio),
            data_fim: formatMonthParam(dataFim)
        });

        navigate(`/BISS/realEstimadoInversor?${searchParams.toString()}`);
    };

    const columns = useMemo(getColumns, []);

    if (!initialDataLoaded) {
        return (
            <Box className='mainBox real-estimated-bi-page'>
                <PageHeader>Relatório SPE</PageHeader>
                {circularProgress()}
            </Box>
        );
    }

    return (
        <Box className='mainBox real-estimated-bi-page'>
            <PageHeader>Relatório SPE</PageHeader>

            <PageIntro eyebrow='BI SS' title='Relatório SPE'>
                Geração e resultado de cada SPE no período, com exportação para planilha.
            </PageIntro>

            <Box className='real-estimated-bi-shell'>
                <Box className='real-estimated-bi-filters real-estimated-bi-filters-with-action'>
                    <MonthRangePicker
                        startDate={dataInicio}
                        endDate={dataFim}
                        minDate={minMonth}
                        maxDate={getPreviousDayMonthStart()}
                        disabled={isLoading}
                        onApply={handleDateRangeApply}
                    />

                    <Autocomplete
                        disableClearable
                        options={empresaOptions}
                        value={selectedEmpresa}
                        onChange={(event, value) => handleEmpresaChange(value)}
                        getOptionLabel={(option) => getOptionLabel(option, 'Todas')}
                        renderInput={(params) => <TextField {...params} label='SPE' />}
                    />

                    <Autocomplete
                        disableClearable
                        options={siteOptions}
                        value={selectedSite}
                        onChange={(event, value) => handleSiteChange(value)}
                        getOptionLabel={(option) => getOptionLabel(option, 'Todos')}
                        renderInput={(params) => <TextField {...params} label='Site' />}
                    />

                    <Button
                        className='real-estimated-bi-drill-button'
                        variant='contained'
                        disabled={!canDrillThrough}
                        endIcon={<KeyboardArrowRightRoundedIcon />}
                        onClick={handleDrillThrough}
                    >
                        Aderência de inversores
                    </Button>
                </Box>

                <Box className='real-estimated-bi-summary'>
                    <Box>
                        <span><SpeedRoundedIcon /> Aderência à energia garantida</span>
                        <strong>{formatRatio(summary.aderenciaGarantida)}</strong>
                    </Box>
                    <Box>
                        <span><EnergySavingsLeafRoundedIcon /> Geração (kWh)</span>
                        <strong>{formatDecimal(summary.geracao)}</strong>
                    </Box>
                    <Box>
                        <span><QueryStatsRoundedIcon /> Aderência ao P50</span>
                        <strong>{formatRatio(summary.aderenciaP50)}</strong>
                    </Box>
                    <Box>
                        <span><BoltRoundedIcon /> Fator de capacidade</span>
                        <strong>{formatRatio(summary.fatorCapacidade)}</strong>
                    </Box>
                    <Box>
                        <span><BoltRoundedIcon /> Disponibilidade energética técnica</span>
                        <strong>{formatRatio(summary.disponibilidadeTecnica)}</strong>
                    </Box>
                    <Box>
                        <span><BoltRoundedIcon /> Disponibilidade energética O&amp;M</span>
                        <strong>{formatRatio(summary.disponibilidadeOM)}</strong>
                    </Box>
                    <Box>
                        <span><SpeedRoundedIcon /> Inversor-dias parados sem falha</span>
                        <strong>{summary.inversor_dias_sem_falha}</strong>
                    </Box>
                </Box>

                <RelatorioSpeChart rows={chartRows} />

                <Box className='real-estimated-bi-table-panel'>
                    <DataGrid
                        className='real-estimated-bi-grid'
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => `${row.periodo}-${row.empresa_id}-${row.site_id}`}
                        loading={isLoading}
                        disableRowSelectionOnClick
                        pageSizeOptions={[100, 150]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 100
                                }
                            }
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default RelatorioSpeBI;
